import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useGanttTransform } from './useGanttTransform';
import { applyServiceScheduledDate } from '../utils/workOrderGroups';
import { getServicesGroupedSearch, getCountOfServiceList } from 'services/services/serviceLists';
import { showError } from '../../../../../redux/common/actions';

// Configuration constants
const PAGE_SIZE = 30; // Work order groups per page

export const useGanttData = (filters, refreshRevision) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const isLoadingRef = useRef(false); // Track if we're currently loading
	
	// State management
	const [services, setServices] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState(null);
	const [totalCount, setTotalCount] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [hasMorePages, setHasMorePages] = useState(true);

	// Fetch services count
	const fetchCount = useCallback(async () => {
		try {
			const response = await getCountOfServiceList(filters);
			if (response.status) {
				setTotalCount(response.data || 0);
			}
		} catch (err) {
			console.error("Failed to fetch count:", err);
		}
	}, [filters]);

	/**
	 * Fetches services using grouped search API for Gantt mode
	 * @param {number} page - Page number to fetch
	 * @param {boolean} append - Whether to append to existing services or replace
	 */
	const fetchServices = useCallback(
		async (page = 1, append = false) => {
			// Prevent concurrent requests
			if (isLoadingRef.current) {
				return;
			}
			isLoadingRef.current = true;

			try {
				if (append) {
					setLoadingMore(true);
				} else {
					setLoading(true);
				}
				setError(null);

				// Use new GroupedSearch endpoint
				const response = await getServicesGroupedSearch({
					pageNumber: page,
					pageSize: PAGE_SIZE,
					...filters,
					sortField: "scheduledDate",
					sort: "asc",
				});

				if (response.status) {
					const workOrderGroups = response.data || [];

					if (append) {
						// Append work order groups (not flattened)
						setServices((prev) => [...prev, ...workOrderGroups]);
						setCurrentPage(page);
						// Continue if we got a full page of groups
						setHasMorePages(workOrderGroups.length === PAGE_SIZE);
					} else {
						// Initial load
						setServices(workOrderGroups);
						setCurrentPage(page);
						setHasMorePages(workOrderGroups.length === PAGE_SIZE);
					}
				}
			} catch (err) {
				setError(err.message);
				dispatch(showError(t("message.failedToLoadServices")));
				setHasMorePages(false);
			} finally {
				setLoading(false);
				setLoadingMore(false);
				isLoadingRef.current = false;
			}
		},
		[filters, dispatch, t],
	);

	// Load more services
	const loadMore = useCallback(() => {
		// Load next page of work order groups
		if (!loadingMore && hasMorePages) {
			fetchServices(currentPage + 1, true);
		}
	}, [loadingMore, hasMorePages, currentPage, fetchServices]);

	// Reset and fetch when filters change
	useEffect(() => {
		const resetAndFetch = async () => {
			setCurrentPage(1);
			setServices([]);
			setHasMorePages(true);
			await fetchCount();
			await fetchServices(1);
		};

		resetAndFetch();
	}, [fetchCount, fetchServices, refreshRevision]);

	const servicesRef = useRef(services);
	servicesRef.current = services;

	// The Gantt already shows the moved bar; only the loaded record needs the
	// saved date, for when more work orders page in and the bars are rebuilt
	const applyRescheduledService = useCallback((serviceId, scheduledDate) => {
		if (!scheduledDate) return;

		applyServiceScheduledDate(servicesRef.current, serviceId, scheduledDate);
	}, []);

	const { ganttData, taskTypes } = useGanttTransform(services);

	return {
		ganttData,
		taskTypes,
		services,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMorePages,
		loadMore,
		applyRescheduledService,
	};
};
